/**
 * 授權「模組（entitlement map）」的顯示邏輯。
 *
 * ★★ 批93 多產品化：**這裡不再有任何模組／子項的中文標籤常數**。
 *   模組有哪些鍵、叫什麼中文名、哪些是白名單型（有子項）、子項可不可勾選，全部由
 *   **該產品的憑證設計**（`ProductSpec`，`GET /api/admin/products`）提供——不同產品
 *   有不同的模組語彙，寫死在前端的那一份必然只服務得了 CS 一個產品。
 *   （批93 之前的 `MODULE_LABELS`／`CHANNEL_LABELS`／`AI_LABELS`／`OAUTH_LABELS`／
 *   `SCOPED_MODULES` 已整組刪除，凍結副本存在後端
 *   `tests/test_cs_seed_matches_legacy_constants.py` 供對拍。）
 *
 * 所有函式都吃 `spec | null`：spec 還沒載回來（或呼叫者拿不到產品，例如部署頁的孤兒列）
 * 時一律**回原始英文 key**，絕不擲錯、也絕不猜。
 */
import type { ModuleSpec, ProductSpec } from '@/api/ls'

function moduleOf(spec: ProductSpec | null | undefined, key: string): ModuleSpec | null {
  return (spec?.modules ?? []).find((m) => m.key === key) ?? null
}

/** 模組 key → 中文名；spec 沒有這個鍵（打錯字／別的產品的鍵）→ 回原字串。 */
export function moduleLabel(spec: ProductSpec | null | undefined, key: string): string {
  return moduleOf(spec, key)?.label ?? key
}

function isAllScope(scope: unknown): boolean {
  return typeof scope !== 'string' || !scope.trim() || scope.trim() === '*'
}

/**
 * scope 值轉中文：`*`／空→全部；逗號清單→各子項中文；非字串（JSONB 不保證）→空字串。
 * 子項名查該模組的 `items`（含 `selectable:false` 的——既有授權可能帶著它）。
 */
export function scopeLabel(
  spec: ProductSpec | null | undefined,
  key: string,
  scope: unknown,
): string {
  if (typeof scope !== 'string') return ''
  if (isAllScope(scope)) return '全部'
  const items = moduleOf(spec, key)?.items ?? []
  return scope
    .split(',')
    .map((s) => {
      const t = s.trim()
      return items.find((i) => i.key === t)?.label ?? t
    })
    .filter(Boolean)
    .join('、')
}

export interface ModuleEntry {
  key: string
  label: string
  detail: string
  /** 有子項白名單概念（憑證設計標 `kind: "scoped"`）。 */
  scoped: boolean
  /** 該鍵不在這個產品的憑證設計裡（打錯字／別的產品的鍵）——畫面標警示色。 */
  unknown: boolean
  /** 畫面用成品字串（見 fmtModulesZh）。 */
  text: string
}

/**
 * entitlement map → 顯示清單。`text` 規則：
 * - 二態模組（`kind: "binary"`）且 `*` → 只印名稱，例：`知識庫`
 * - 白名單模組（`kind: "scoped"`）且範圍 `*` → `AI 模型：全部`
 * - 明確白名單 → `進線通路：LINE 官方帳號、官網 Web Chat`（不分模組類型都印，那是實質限制）
 *
 * ★ 為什麼 binary 的「全部」不印：那是噪音（有授權就是全開）。這條規則與 CS 端
 *   `lib/licenseDisplay.ts` 一致，只是判準從寫死的 `SCOPED_MODULES` 換成憑證設計。
 */
export function fmtModulesZh(
  spec: ProductSpec | null | undefined,
  modules: Record<string, unknown> | null | undefined,
): ModuleEntry[] {
  if (!modules) return []
  return Object.entries(modules).map(([k, v]) => {
    const mod = moduleOf(spec, k)
    const label = mod?.label ?? k
    const detail = scopeLabel(spec, k, v)
    const scoped = mod?.kind === 'scoped'
    const text = isAllScope(v) && !scoped ? label : detail ? `${label}：${detail}` : label
    return { key: k, label, detail, scoped, unknown: !mod, text }
  })
}

/** entitlement map → 單行字串（給表格 cell）。 */
export function fmtModulesLine(
  spec: ProductSpec | null | undefined,
  modules: Record<string, unknown> | null | undefined,
): string {
  const parts = fmtModulesZh(spec, modules)
  if (!parts.length) return '—'
  return parts.map((p) => p.text).join('；')
}

export const STATUS_LABELS: Record<string, string> = {
  active: '生效中',
  revoked: '已撤銷',
  superseded: '已被取代',
  pending: '待核發',
  // 推導態（見 effectiveStatus）
  pending_activation: '等待啟用',
  expired: '已到期',
}
export function statusLabel(s: string): string {
  return STATUS_LABELS[s] ?? s
}

/**
 * 授權「有效狀態」——DB 只有 active/revoked/superseded，「等待啟用」與「已到期」是推導：
 * - active 且 expires_at 空白＝已簽發、客戶尚未下載／取回啟用（到期日取回時才蓋上）
 * - active 且到期日已過＝已到期（產品端會轉唯讀）
 */
export function effectiveStatus(
  lic: { status: string; expires_at: string | null },
  now: number = Date.now(),
): string {
  if (lic.status !== 'active') return lic.status
  if (!lic.expires_at) return 'pending_activation'
  return Date.parse(lic.expires_at) <= now ? 'expired' : 'active'
}

/**
 * 「到期臨近」的天數門檻（批80 批B3）。
 *
 * 對齊後端 `settings.license_warn_days`（預設 30）——那是 LS 開
 * `license_expiring` 告警的界線。前端這個桶要跟告警看到同一批授權，否則
 * 「告警說要續約、清單卻篩不出來」會讓人以為其中一邊壞了。
 * ⚠️ 改這個數字前先確認後端 env `IPO_LS_LICENSE_WARN_DAYS` 沒被調過。
 */
export const LICENSE_WARN_DAYS = 30

/**
 * 「到期臨近」＝**仍有效**（`effectiveStatus === 'active'`）且距到期 ≤30 天。
 *
 * 刻意與「使用中」桶**重疊**：這不是一種狀態，是一張待辦清單。已到期／已撤銷的
 * 不算（那些要看的是別的桶），從未啟用（`expires_at` 空）的也不算（還沒開始計時）。
 */
export function isExpiringSoon(
  lic: { status: string; expires_at: string | null },
  now: number = Date.now(),
): boolean {
  if (effectiveStatus(lic, now) !== 'active' || !lic.expires_at) return false
  const days = (Date.parse(lic.expires_at) - now) / 86_400_000
  return days <= LICENSE_WARN_DAYS
}

/** 距到期還有幾天（向上取整；已過期為負數、無到期日為 null）。 */
export function daysUntilExpiry(
  expiresAt: string | null,
  now: number = Date.now(),
): number | null {
  if (!expiresAt) return null
  const t = Date.parse(expiresAt)
  if (Number.isNaN(t)) return null
  return Math.ceil((t - now) / 86_400_000)
}

export const MODE_LABELS: Record<string, string> = {
  online: '線上',
  airgapped: '離線（氣隙）',
}
export function modeLabel(m: string): string {
  return MODE_LABELS[m] ?? m
}
